import {
  autoUpdate,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from '@floating-ui/react'
import { ChevronDown } from 'lucide-react'
import * as React from 'react'
import { ComponentProps, CSSProperties } from 'react'
import { useWebchatStore } from '../../../stores'
import { webchatClasses } from '../../../styles/classes'
import { DropdownBlock, type CommonBlockProps } from '../../../types'

type Props = DropdownBlock & ComponentProps<'div'> & CommonBlockProps

export const Dropdown = ({ options, reusable, sendMessage, isReadOnly }: Props) => {
  const messageContainerRef = useWebchatStore((s) => s.messageContainerRef)
  const portalContainer = useWebchatStore((s) => s.portalContainer)

  const [isOpen, setIsOpen] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null)
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null)

  const {
    message: {
      blocks: { dropdown },
    },
  } = webchatClasses

  const optionsDict = options.reduce(
    (acc, option) => {
      acc[option.label] = option.value
      return acc
    },
    {} as { [key: string]: string }
  )

  const labels = optionsDict ? Object.keys(optionsDict) : []

  const onOpenChange = (open: boolean) => {
    if (selectedIndex !== null && !reusable) {
      setIsOpen(false)
    } else {
      setIsOpen(open)
    }
  }

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      shift({
        padding: 10,
        boundary: messageContainerRef?.current ?? undefined,
        crossAxis: true,
      }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            minWidth: `${rects.reference.width}px`,
          })
        },
        padding: 10,
      }),
    ],
  })

  const listRef = React.useRef<Array<HTMLElement | null>>([])
  const listContentRef = React.useRef(labels)
  const isTypingRef = React.useRef(false)

  const click = useClick(context, { event: 'mousedown' })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'listbox' })
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    // This is a large list, allow looping.
    loop: true,
  })
  const typeahead = useTypeahead(context, {
    listRef: listContentRef,
    activeIndex,
    selectedIndex,
    onMatch: isOpen ? setActiveIndex : setSelectedIndex,
    onTypingChange(isTyping) {
      isTypingRef.current = isTyping
    },
  })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    dismiss,
    role,
    listNav,
    typeahead,
    click,
  ])

  const handleSelect = (index: number) => {
    setSelectedIndex(index)
    if (labels[index] !== undefined) {
      void sendMessage?.({
        type: 'text',
        text: labels[index] ?? '',
        value: optionsDict[labels[index]] ?? '',
      })
    }
    setIsOpen(false)
  }

  return (
    <>
      <div
        tabIndex={0}
        ref={refs.setReference}
        aria-labelledby="select-label"
        aria-autocomplete="none"
        data-disabled={selectedIndex !== null && !reusable ? '' : undefined}
        style={isReadOnly ? { pointerEvents: 'none' } : undefined}
        {...dropdown.button?.container}
        {...getReferenceProps()}
      >
        <span {...dropdown.button?.text}>{selectedIndex ? labels[selectedIndex] : 'Select...'}</span>
        <ChevronDown {...dropdown.button?.icon} />
      </div>
      {isOpen && (
        <FloatingPortal root={portalContainer ?? undefined}>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              className={dropdown.content?.container?.className}
              style={{ ...floatingStyles, pointerEvents: 'auto' } as CSSProperties}
              {...getFloatingProps()}
            >
              {labels.map((value, i) => (
                <div
                  key={value}
                  ref={(node) => {
                    listRef.current[i] = node
                  }}
                  role="option"
                  tabIndex={i === activeIndex ? 0 : -1}
                  aria-selected={i === selectedIndex && i === activeIndex}
                  data-active={i === activeIndex ? '' : undefined}
                  {...dropdown.content?.item}
                  {...getItemProps({
                    onClick() {
                      handleSelect(i)
                    },
                    onKeyDown(event) {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                        handleSelect(i)
                      }
                      if (event.key === ' ' && !isTypingRef.current) {
                        event.preventDefault()
                        handleSelect(i)
                      }
                    },
                  })}
                >
                  {value}
                </div>
              ))}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  )
}
